import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'
import { resourcesCopy } from '@/copy/resources'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { CheckboxField, TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { resourcesFor } from '../../api/resources'
import { useToastStore } from '../../store/toast'
import type { TodoItem } from '../../types/api'
import type {
  Fields,
  ResourceEditExtrasProps,
  ResourceFormDescriptor,
} from './shared'
import styles from './TodoFields.module.css'

/**
 * todo_list has no create-time config; its items are managed out-of-band via
 * their own endpoints, so the edit UI + mutations live in `EditExtras` here
 * rather than being special-cased in the form page.
 */
// eslint-disable-next-line react-refresh/only-export-components -- co-located descriptor
export const todoForm: ResourceFormDescriptor = {
  Fields: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required by ResourceFormDescriptor signature
  toBody: (_f: Fields) => ({ items: [] }),
  fromResource: () => ({}),
  skipDataOnUpdate: true,
  EditExtras: TodoEditExtras,
}

/** Edit-mode todo item manager: owns its add/remove/toggle mutations. */
function TodoEditExtras({ scope, resourceId, data }: ResourceEditExtrasProps) {
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const api = resourcesFor(scope)
  const items = (data as { items?: TodoItem[] } | undefined)?.items ?? []
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['resources', scope.kind, scope.id] })

  const addMut = useMutation({
    mutationFn: (text: string) => api.addTodo(resourceId, text),
    onSuccess: invalidate,
    onError: (e: Error) =>
      addToast(resourcesCopy.todo.addFailed(e.message), 'error'),
  })
  const removeMut = useMutation({
    mutationFn: (itemId: string) => api.removeTodo(resourceId, itemId),
    onSuccess: invalidate,
    onError: (e: Error) =>
      addToast(resourcesCopy.todo.removeFailed(e.message), 'error'),
  })
  const toggleMut = useMutation({
    mutationFn: ({
      itemId,
      completed,
    }: {
      itemId: string
      completed: boolean
    }) => api.setTodoCompleted(resourceId, itemId, completed),
    onSuccess: invalidate,
    onError: (e: Error) =>
      addToast(resourcesCopy.todo.updateFailed(e.message), 'error'),
  })

  return (
    <TodoItemEditor
      items={items}
      onAdd={(text) => addMut.mutate(text)}
      onRemove={(itemId) => removeMut.mutate(itemId)}
      onToggle={(itemId, completed) => toggleMut.mutate({ itemId, completed })}
      isAddPending={addMut.isPending}
      removingId={removeMut.isPending ? removeMut.variables : undefined}
      isTogglePending={toggleMut.isPending}
    />
  )
}

interface TodoItemEditorProps {
  items: TodoItem[]
  onAdd: (text: string) => void
  onRemove: (itemId: string) => void
  onToggle: (itemId: string, completed: boolean) => void
  isAddPending: boolean
  /** The item whose remove is in flight. */
  removingId?: string
  isTogglePending: boolean
}

/**
 * Inline todo-item manager shown in edit mode. Lives here (next to the kind it
 * belongs to) instead of inside the form page. Not nested in a `<form>` — it
 * sits inside the page's outer form and HTML forbids nested forms. The list
 * ends with its add row, which turns into the new item's field in place
 * (D213: a list creates from its end); Enter adds and keeps the field open,
 * Escape or leaving it empty turns it back into the add row.
 */
export function TodoItemEditor({
  items,
  onAdd,
  onRemove,
  onToggle,
  isAddPending,
  removingId,
  isTogglePending,
}: TodoItemEditorProps) {
  const [newItem, setNewItem] = useState('')
  const [adding, setAdding] = useState(false)
  const labelId = useId()
  const addRowId = useId()
  const focusAddRow = useRef(false)

  useEffect(() => {
    if (adding || !focusAddRow.current) return
    focusAddRow.current = false
    document.getElementById(addRowId)?.focus()
  }, [adding, addRowId])

  function submitNewItem() {
    const text = newItem.trim()
    if (!text) return
    onAdd(text)
    setNewItem('')
    // TextField's inputProps omits `ref`, so refocus by id.
    document.getElementById('todo-new')?.focus()
  }

  function closeNewItem(returnFocus: boolean) {
    focusAddRow.current = returnFocus
    setNewItem('')
    setAdding(false)
  }

  return (
    <Stack gap="space-8" role="group" aria-labelledby={labelId}>
      <Text id={labelId} tone="text-sub">
        {resourcesCopy.todo.items}
      </Text>
      {items.length === 0 && (
        <EmptyState variant="inline" description={resourcesCopy.todo.empty} />
      )}
      {items.map((item) => (
        <Inline
          key={item.id}
          gap="space-8"
          wrap={false}
          className={styles.item}
        >
          <div className={styles.grow}>
            <CheckboxField
              id={`todo-${item.id}`}
              label={item.text}
              checked={item.completed}
              onChecked={(c) => onToggle(item.id, c)}
              inputProps={{ disabled: isTogglePending }}
            />
          </div>
          <span data-row-actions>
            <IconButton
              label={resourcesCopy.todo.remove(item.text)}
              loading={removingId === item.id}
              onClick={() => onRemove(item.id)}
            >
              <Icon name="trash" />
            </IconButton>
          </span>
        </Inline>
      ))}
      {adding ? (
        <Inline gap="space-8" align="end" wrap={false}>
          <div className={styles.grow}>
            <TextField
              id="todo-new"
              label={resourcesCopy.todo.newItem}
              value={newItem}
              onValue={setNewItem}
              inputProps={{
                autoFocus: true,
                onKeyDown: (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    submitNewItem()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    closeNewItem(true)
                  }
                },
                onBlur: () => {
                  if (!newItem.trim()) closeNewItem(false)
                },
              }}
            />
          </div>
          <Button
            variant="outline"
            disabled={!newItem.trim()}
            loading={isAddPending}
            onClick={submitNewItem}
          >
            {resourcesCopy.todo.add}
          </Button>
        </Inline>
      ) : (
        <div>
          <Button id={addRowId} variant="ghost" onClick={() => setAdding(true)}>
            <Icon name="plus" size="icon-embedded" />
            {resourcesCopy.todo.addItem}
          </Button>
        </div>
      )}
    </Stack>
  )
}
